import React from 'react';
import { TCITooltip } from '@/components/ui/Tooltip';
import I18n from 'modules/i18n';
import useKeydownAndClick from '@/hooks/useKeydownAndClick';
import Icon from '@/components/ui/Icon';
import { COLORS } from '@/components/ui/ui.constants';
import key from 'images/icons/key.png';
import keyDisabled from 'images/icons/key-disabled.png';
import cn from '@/utils/cn';
import { ImpersonateStudentProps } from './types';
import styles from './ImpersonateStudent.module.scss';

interface ImpersonateStudentButtonProps extends ImpersonateStudentProps {
  buttonClass?: string;
  iconClass?: string;
  modal: ModalManager;
  tooltipPlacement: 'top' | 'bottom';
  useNewIcon?: boolean;
}

interface ImpersonateTooltipContentProps {
  enabled: boolean;
  hasStudent: boolean;
}

const ImpersonateTooltipContent = ({ enabled, hasStudent }: ImpersonateTooltipContentProps) => {
  if (!enabled) return <>{I18n.t('return_to_admin')}</>;
  return <>{hasStudent ? I18n.t('sign_in_as_this_student') : I18n.t('view_as_student')}</>;
};

const ImpersonateStudentButton = ({
  student, enabled, modal, iconClass, buttonClass, tooltipPlacement, useNewIcon = false,
}: ImpersonateStudentButtonProps) => {
  const srText = student ? I18n.t('sign_in_as', { name: student.name }) : I18n.t('view_as_student');
  const btnClass = buttonClass || cn(
    'tw-bg-transparent',
    styles.button,
    { '!tw-w-8 !tw-h-8 tw-inline-flex tw-items-center tw-justify-center': useNewIcon },
  );
  const imgClass = iconClass || styles.warningIcon;

  return (
    <TCITooltip side={tooltipPlacement}>
      <TCITooltip.Trigger asChild>
        <button
          aria-controls="impersonateStudentModal"
          aria-disabled={!enabled}
          aria-haspopup="dialog"
          aria-label={I18n.t('sign_in_as_student')}
          className={btnClass}
          type="button"
          {...useKeydownAndClick(enabled ? modal.open : null)}
        >
          {!useNewIcon && <img alt="" className={imgClass} src={enabled ? key : keyDisabled} />}
          {useNewIcon && (
            <Icon
              className={`${iconClass} tw-w-full tw-h-full tw-m-0`}
              fill={enabled ? COLORS['tci-purple'] : COLORS['dark-grey']}
              icon="Key"
            />
          )}
          <span className="sr-only">{srText}</span>
        </button>
      </TCITooltip.Trigger>
      <TCITooltip.Content>
        <ImpersonateTooltipContent enabled={enabled} hasStudent={!!student} />
      </TCITooltip.Content>
    </TCITooltip>
  );
};

export default ImpersonateStudentButton;
